import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import styles from "../../../styles/components/PaymentPlansSection.module.css";
import ChoiceChip from "../../../ui/selectors/ChoiceChip";
import StripeCheckoutModal from "../billing/StripeCheckoutModal";
import PlanChangePreviewModal from "../billing/PlanChangePreviewModal";
import {
  getPaymentPlansByAccount,
  previewChangePlan,
  changePlan,
  createSubscriptionIntent,
} from "../../../services/billing.service";

const PaymentPlansSection = ({
  accounts = [],
  selectedAccount,
  onSelectedAccountChange,
  summary,
  refreshSummary,
}) => {
  // refreshAccounts re-fetchea my-supervised (plan actual de cada cuenta) a nivel global.
  const { refreshAccounts } = useAuth() || {};

  const [plansData, setPlansData] = useState({
    current_plan: null,
    next_billing_date: null,
    plans: [],
  });
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [error, setError] = useState("");
  const [billingType, setBillingType] = useState("mensual");

  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState("");

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [changePreview, setChangePreview] = useState(null);
  const [confirmingChange, setConfirmingChange] = useState(false);
  const [loadingPlanCode, setLoadingPlanCode] = useState(null);
  const [planNameOverrides, setPlanNameOverrides] = useState({});

  const scrollerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const dragStartXRef = useRef(0);
  const scrollStartLeftRef = useRef(0);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!isDraggingRef.current || !scrollerRef.current) return;
      const deltaX = event.clientX - dragStartXRef.current;
      if (Math.abs(deltaX) > 3) hasDraggedRef.current = true;
      scrollerRef.current.scrollLeft = scrollStartLeftRef.current - deltaX;
      scrollerRef.current.style.cursor = "grabbing";
    };

    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      if (scrollerRef.current) scrollerRef.current.style.cursor = "grab";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleScrollerMouseDown = (event) => {
    if (!scrollerRef.current) return;
    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    dragStartXRef.current = event.clientX;
    scrollStartLeftRef.current = scrollerRef.current.scrollLeft;
    scrollerRef.current.style.cursor = "grabbing";
  };

  const handleScrollerClickCapture = (event) => {
    if (hasDraggedRef.current) {
      event.stopPropagation();
      hasDraggedRef.current = false;
    }
  };

  const uiInterval = billingType === "anual" ? "annual" : "monthly";
  const currentBillingInterval = plansData.current_plan?.billing_interval;

  const isCurrentPlanTier = (plan) => plan.code === plansData.current_plan?.code;
  const isPlanEffectivelyCurrent = (plan) =>
    isCurrentPlanTier(plan) && currentBillingInterval === uiInterval;

  const applyPlansResponse = (accountId, response) => {
    const newData = {
      current_plan: response?.current_plan || null,
      next_billing_date: response?.next_billing_date || null,
      plans: Array.isArray(response?.plans) ? response.plans : [],
    };
    setPlansData(newData);
    if (accountId && newData.current_plan?.name) {
      setPlanNameOverrides((prev) => ({ ...prev, [accountId]: newData.current_plan.name }));
    }
  };

  const refreshPlansData = async () => {
    setLoadingPlans(true);
    try {
      const [response] = await Promise.all([
        getPaymentPlansByAccount(selectedAccount.id),
        refreshSummary?.(),
        refreshAccounts?.(),
      ]);
      applyPlansResponse(selectedAccount.id, response);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleConfirmChangePlan = async () => {
    if (!changePreview) return;

    try {
      setConfirmingChange(true);
      setError("");

      const currentPlanStatus = plansData?.current_plan?.status;
      const needsNewSubscription = !currentPlanStatus || currentPlanStatus !== "active";

      if (needsNewSubscription) {
        const intent = await createSubscriptionIntent({
          account_id: selectedAccount.id,
          plan_code: changePreview.new_plan_code,
          billing_interval: uiInterval,
        });

        if (intent?.no_payment_required) {
          setPreviewModalOpen(false);
          setChangePreview(null);
          setLoadingPlans(true);

          const [response] = await Promise.all([
            getPaymentPlansByAccount(selectedAccount.id),
            refreshSummary?.(),
            refreshAccounts?.(),
          ]);
          applyPlansResponse(selectedAccount.id, response);
          return;
        }

        const clientSecret = intent?.client_secret;

        if (!clientSecret) {
          throw new Error("No se recibió client_secret desde subscription-intent.");
        }

        setPreviewModalOpen(false);
        setChangePreview(null);
        setCheckoutClientSecret(clientSecret);
        setCheckoutModalOpen(true);
        return;
      }

      if (!changePreview.preview_id) {
        throw new Error("No se pudo procesar el cambio de plan.");
      }

      await changePlan({
        account_id: selectedAccount.id,
        preview_id: changePreview.preview_id,
        billing_interval: uiInterval,
      });

      setPreviewModalOpen(false);
      setChangePreview(null);
      setLoadingPlans(true);

      const [response] = await Promise.all([
        getPaymentPlansByAccount(selectedAccount.id),
        refreshSummary?.(),
        refreshAccounts?.(),
      ]);
      applyPlansResponse(selectedAccount.id, response);
    } catch (err) {
      console.error("Error confirmando cambio:", err);
      setError(err.message || "No se pudo cambiar el plan.");
    } finally {
      setConfirmingChange(false);
      setLoadingPlans(false);
    }
  };

  const handleChangePlan = async (plan) => {
    if (isPlanEffectivelyCurrent(plan) || loadingPlanCode) return;

    try {
      setError("");
      setLoadingPlanCode(plan.code);

      const currentMonthlyPrice = Number(
        plansData?.current_plan?.prices?.find((p) => p.interval_code === "monthly")?.price || 0
      );
      const isCurrentPlanFree = !plansData?.current_plan || currentMonthlyPrice === 0;

      if (isCurrentPlanFree) {
        const intent = await createSubscriptionIntent({
          account_id: selectedAccount.id,
          plan_code: plan.code,
          billing_interval: uiInterval,
        });

        if (intent?.no_payment_required) {
          await refreshPlansData();
          return;
        }

        const clientSecret = intent?.client_secret;
        if (!clientSecret) {
          throw new Error("No se recibió client_secret desde subscription-intent.");
        }

        setCheckoutClientSecret(clientSecret);
        setCheckoutModalOpen(true);
        return;
      }

      const preview = await previewChangePlan({
        account_id: selectedAccount.id,
        new_plan_code: plan.code,
        billing_interval: uiInterval,
      });

      const nextInvoiceCents =
        preview.next_invoice_total_cents ||
        Math.round(
          Number(
            billingType === "anual"
              ? plan.price_annual || 0
              : plan.price_monthly || 0
          ) * 100
        );

      setChangePreview({
        ...preview,
        new_plan_code: plan.code,
        current_plan_name: plansData?.current_plan?.name || preview.current_plan_code,
        new_plan_name: plan.name || preview.new_plan_code,
        next_invoice_total_cents: nextInvoiceCents,
      });

      setPreviewModalOpen(true);
    } catch (err) {
      console.error("Error obteniendo preview:", err);
      setError(err.message || "No se pudo obtener el preview.");
    } finally {
      setLoadingPlanCode(null);
    }
  };

  useEffect(() => {
    const fetchPlans = async () => {
      if (!selectedAccount?.id) return;

      try {
        setLoadingPlans(true);
        setError("");

        const response = await getPaymentPlansByAccount(selectedAccount.id);

        setPlansData({
          current_plan: response?.current_plan || null,
          next_billing_date: response?.next_billing_date || null,
          plans: Array.isArray(response?.plans) ? response.plans : [],
        });
      } catch (err) {
        console.error("Error obteniendo planes:", err);
        setError("No se pudieron cargar los planes.");
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPlans();
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setPlansData({
        current_plan: null,
        next_billing_date: null,
        plans: [],
      });
      setError("");
      setLoadingPlans(false);
    }
  }, [selectedAccount]);

  const featureLabels = useMemo(() => {
    if (!plansData.plans.length) return [];

    const firstPlanWithFeatures = plansData.plans.find(
      (plan) => Array.isArray(plan.features) && plan.features.length
    );

    return firstPlanWithFeatures?.features || [];
  }, [plansData.plans]);

  if (!selectedAccount) {
    return (
      <div className={styles.sectionContent}>
        {!accounts.length ? (
          <div className={styles.emptyState}>No hay cuentas para mostrar.</div>
        ) : null}

        <div className={styles.accountsList}>
          {accounts.map((account) => (
            <div key={account.id} className={styles.accountItem}>
              <button
                type="button"
                className={styles.accountCardButton}
                onClick={() => onSelectedAccountChange?.(account)}
              >
                <div className={styles.accountCard}>
                  <div className={styles.accountLeft}>
                    <div className={styles.accountInfo}>
                      <div className={styles.accountTopLine}>
                        <div className={styles.accountLogo}>
                          <span>
                            {(account.name || "?").charAt(0).toUpperCase()}
                          </span>
                        </div>

                         <div className={styles.accountInfo}>
                          <div className={styles.accountCompany}>
                            {account.name || "Sin nombre"}
                          </div>
                          <div className={styles.accountPlanInfo}>
                            {planNameOverrides[account.id] || account.current_plan_name || "Sin plan"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <span className={styles.linkButton}>Cambiar plan</span>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const formatScheduledDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const currentPlanBillingEntry = plansData.current_plan?.prices?.find(
    (p) => p.interval_code === plansData.current_plan?.billing_interval
  );
  const currentPlanCurrency = (currentPlanBillingEntry?.currency || "usd").toUpperCase();
  const currentPlanBillingSuffix =
    plansData.current_plan?.billing_interval === "monthly" ? "/mes" : "/año";

  return (
    <div className={styles.plansScreen}>
      {loadingPlans ? (
        <div className={styles.stateBox}>Cargando planes...</div>
      ) : error ? (
        <div className={styles.stateBoxError}>{error}</div>
      ) : (
        <>
          {plansData.current_plan ? (
            <div className={styles.currentPlanRow}>
              <div className={styles.currentPlanBlock}>
                <div className={styles.currentPlanLabel}>Tu plan actual</div>

                <div className={styles.currentPlanName}>
                  {plansData.current_plan.name}
                </div>

                <div className={styles.currentPlanMeta}>
                  {`$${Number(currentPlanBillingEntry?.price || 0).toLocaleString("en-US")} ${currentPlanCurrency} ${currentPlanBillingSuffix}`}

                  {plansData.next_billing_date
                    ? ` - Próxima facturación: ${plansData.next_billing_date}`
                    : ""}
                </div>

                {summary?.has_scheduled_change && (
                  <div className={styles.scheduledChangeBanner}>
                    Cambio programado a{" "}
                    <strong>{summary.scheduled_plan_name || summary.scheduled_plan}</strong>{" "}
                    el {formatScheduledDate(summary.scheduled_effective_at)}
                  </div>
                )}
              </div>

              <div className={styles.billingToggle}>
                <div className={styles.billingToggleGroup}>
                  <ChoiceChip
                    label="Mensual"
                    selected={billingType === "mensual"}
                    onClick={() => setBillingType("mensual")}
                  />
                  <ChoiceChip
                    label="Anual"
                    selected={billingType === "anual"}
                    onClick={() => setBillingType("anual")}
                  />
                </div>
              </div>
            </div>
          ) : null}

          <div className={styles.plansBlock}>
            <div className={styles.plansBlockTitle}>Todos los planes</div>

            <div
              ref={scrollerRef}
              className={styles.plansScroller}
              onMouseDown={handleScrollerMouseDown}
              onClickCapture={handleScrollerClickCapture}
              style={{ cursor: "grab" }}
            >
              <div className={styles.featuresColumn}>
                <div className={styles.featuresColumnHeader} />
                <div className={styles.featuresColumnBody}>
                  {featureLabels.map((feature) => (
                    <div
                      key={feature.code || feature.label}
                      className={styles.featureLabelRow}
                    >
                      {feature.label}
                    </div>
                  ))}
                </div>
              </div>

              {plansData.plans.map((plan) => (
                <div
                  key={plan.code}
                  className={`${styles.planColumn} ${
                    isPlanEffectivelyCurrent(plan) ? styles.planColumnCurrent : ""
                  }`}
                >
                  <div className={styles.planColumnHeader}>
                    {billingType === "anual" && (
                      <div className={styles.planBadge}>
                        <span>
                          {(plan.price_monthly || 0) === 0 ? "Gratis" : "Ahorro: $" + Number((((plan.price_monthly || 0) * 12) - (plan.price_annual || 0))).toLocaleString("en-US")+ "/año"}
                        </span>
                      </div>
                    )}

                    <div className={styles.planTitle}>{plan.name}</div>

                    <div className={styles.planPriceBlock}>
                      {plan.is_enterprise ? (
                        <div className={styles.planCustomPrice}>
                          Pedir Presupuesto
                        </div>
                      ) : (
                        <div className={styles.planPrice}>
                          {billingType === "mensual" ? (
                            <>
                              ${Number(plan.price_monthly || 0).toLocaleString("en-US")}{" "}
                              <span>/mes</span>
                            </>
                          ) : (
                            <>
                              ${Number(
                                (plan.price_annual || 0)
                              ).toLocaleString("en-US")}{" "}
                              <span>/año</span>
                            </>
                          )}
                        </div>
                      )}

                      {billingType === "mensual" && !!plan.annual_billing_text && (
                        <div className={styles.planBillingText}>
                          {plan.annual_billing_text}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      disabled={isPlanEffectivelyCurrent(plan) || !!loadingPlanCode}
                      onClick={() => handleChangePlan(plan)}
                      className={
                        isPlanEffectivelyCurrent(plan)
                          ? styles.planPrimaryButton
                          : styles.planSecondaryButton
                      }
                    >
                      {loadingPlanCode === plan.code
                        ? "Procesando..."
                        : isPlanEffectivelyCurrent(plan)
                          ? "Plan actual"
                          : isCurrentPlanTier(plan)
                            ? billingType === "anual"
                              ? "Cambiar a anual"
                              : "Cambiar a mensual"
                            : plan.cta_label || "Cambiar de plan"}
                    </button>
                  </div>

                  <div className={styles.planFeaturesBody}>
                    {(plan.features || []).map((feature) => (
                      <div
                        key={`${plan.code}-${feature.code || feature.label}`}
                        className={styles.featureValueRow}
                      >
                        {feature.value}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <StripeCheckoutModal
        open={checkoutModalOpen}
        title="Completar suscripción"
        clientSecret={checkoutClientSecret}
        onClose={() => setCheckoutModalOpen(false)}
        onDone={async () => {
          setCheckoutModalOpen(false);
          setCheckoutClientSecret("");
          setLoadingPlans(true);

          try {
            const [response] = await Promise.all([
              getPaymentPlansByAccount(selectedAccount.id),
              refreshSummary?.(),
              refreshAccounts?.(),
            ]);
            applyPlansResponse(selectedAccount.id, response);
          } finally {
            setLoadingPlans(false);
          }
        }}
      />
      <PlanChangePreviewModal
        open={previewModalOpen}
        preview={changePreview}
        loading={confirmingChange}
        onClose={() => {
          if (confirmingChange) return;
          setPreviewModalOpen(false);
          setChangePreview(null);
        }}
        onConfirm={handleConfirmChangePlan}
      />
    </div>
  );
};

export default PaymentPlansSection;
